/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
/**
 * Wire types for the always-rollback SQL preview endpoint
 * ({@code POST /api/i/scripts/sql/preview}). Mirrors the Java DTOs in
 * {@code job/internal/web/SqlPreview*.java}. Consumed by 529.04's
 * useSqlPreview hook.
 */

export const SqlPreviewBinding = {
    query: 'query',
    queryForValue: 'queryForValue',
    update: 'update',
    execute: 'execute',
} as const

export type SqlPreviewBinding = typeof SqlPreviewBinding[keyof typeof SqlPreviewBinding]

export const SqlValueType = {
    Long: 'Long',
    String: 'String',
    Integer: 'Integer',
    BigDecimal: 'BigDecimal',
    Boolean: 'Boolean',
    Instant: 'Instant',
    UUID: 'UUID',
} as const

export type SqlValueType = typeof SqlValueType[keyof typeof SqlValueType]

export interface SqlPreviewRequest {
    sql: string
    binding: SqlPreviewBinding
    paramsLiteral?: string | null
    /** One of {@link SqlValueType} but typed as string to match the Java DTO. */
    valueType?: string | null
}

export interface SqlPreviewResult {
    rowCount?: number | null
    sampleRows?: Array<Record<string, unknown>> | null
    truncated?: boolean | null
    scalarValue?: unknown
    affectedRows?: number | null
    ok?: boolean | null
}

export interface SqlPreview {
    rolledBack: boolean
    durationMs: number
    result: SqlPreviewResult
}

/** Mirrors backend {@code SqlPreviewResponse} top-level shape. */
export interface SqlPreviewResponse {
    rolledBack: boolean
    durationMs: number
    result: SqlPreviewResult
}

export const SqlPreviewErrorCode = {
    error: 'SQL_PREVIEW_ERROR',
    paramsInvalid: 'SQL_PREVIEW_PARAMS_INVALID',
    limitExceeded: 'SQL_PREVIEW_LIMIT_EXCEEDED',
    rateLimitExceeded: 'SQL_PREVIEW_RATE_LIMIT_EXCEEDED',
} as const

export type SqlPreviewErrorCode = typeof SqlPreviewErrorCode[keyof typeof SqlPreviewErrorCode]

export const SqlPreviewLimit = {
    rowsPerQuery: 'rows-per-query',
    statementTimeout: 'statement-timeout',
    responsePayload: 'response-payload',
} as const

export type SqlPreviewLimit = typeof SqlPreviewLimit[keyof typeof SqlPreviewLimit]

export interface SqlPreviewError {
    /** One of {@link SqlPreviewErrorCode} but typed as string to match the Java DTO. */
    errorCode: string
    message: string
    rolledBack: boolean
    /** One of {@link SqlPreviewLimit} but typed as string to match the Java DTO. */
    limit?: string | null
    retryAfterSeconds?: number | null
}
